import { useEffect, useRef, useState } from 'react';
import closeIcon from '../../../../icons/X.svg';
import MyAccountChangeAddressForm from './MyAccountChangeAddressForm';
import { CustomerAddressCreation } from '../MyAccountDeliveryAddressV2/schemas';
import * as translator from '../../../../utils/translator';
import * as http from '../../../../utils/http';
import { MainTracker } from '../../../../PixelTrackers/MainTracker';
import { loadGoogleApi } from '../../../../pages/Shipping/helpers';
import { clientConfig } from '../../../../config';
import PortaledModal from '../../../../components/Modals/PortaledModal';
import ByteplantAddressValidatorModal from '../../../../components/Modals/ByteplantAddressValidatorModal';
import './MyAccountChangeAddress.css';

interface MyAccountChangeAddressProps {
  isOpen: boolean;
  language: string;
  country: string;
  orderDeliveryDetails: {
    first_name: string;
    last_name: string;
    address: string;
    zip: string;
    city: string;
    country_id: string;
    phone: string;
    company: string;
  };
  orderHashID: string;
  onSuccess: () => void;
  onError: (error: any) => void;
  closeable?: boolean;
  onClose?: () => void;
}

const MyAccountChangeAddress = ({
  isOpen,
  language,
  country,
  orderDeliveryDetails,
  orderHashID,
  onSuccess,
  onError,
  closeable = false,
  onClose,
}: MyAccountChangeAddressProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoogleApiLoaded, setIsGoogleApiLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(isOpen);
  const [byteplantState, setByteplantState] = useState<{ showModal: boolean; responseData: ByteplantApiResponse | null }>({ showModal: false, responseData: null });
  const [pendingFormData, setPendingFormData] = useState<CustomerAddressCreation | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsVisible(isOpen);
  }, [isOpen]);

  const countryOptions = [
    { id: orderDeliveryDetails.country_id.toLowerCase(), value: translator.translate(language, "shipping", `ShippingForm/country_name/${country.toLowerCase()}`) }
  ];

  const defaultFormValues: CustomerAddressCreation = {
    address_type: 'regular',
    first_name: orderDeliveryDetails.first_name,
    middle_name: '',
    last_name: orderDeliveryDetails.last_name,
    gender: 0,
    address: orderDeliveryDetails.address,
    zip: orderDeliveryDetails.zip,
    city: orderDeliveryDetails.city,
    country_id: orderDeliveryDetails.country_id,
    phone: orderDeliveryDetails.phone,
    company: orderDeliveryDetails.company || '',
    is_default: false,
  };

  // Load Google Places API and track popup open
  useEffect(() => {
    if (isOpen) {
      MainTracker.track('pv', 'View Pop Up', { 'Pop Up Name': 'Submit a new address for redelivery' });
      loadGoogleApi(() => setIsGoogleApiLoaded(true));
    }
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const postAddressChange = (formData: CustomerAddressCreation) => {
    setIsSubmitting(true);

    MainTracker.track('custom', 'Submit new address for redelivery', {
      'Order ID': orderHashID,
      'Old Address': `${orderDeliveryDetails.address}, ${orderDeliveryDetails.city}, ${orderDeliveryDetails.zip} ${orderDeliveryDetails.country_id}`,
      'New Address': `${formData.address}, ${formData.city}, ${formData.zip} ${orderDeliveryDetails.country_id}`,
    });

    http.postToServer('redelivery-address-change', {
      hashID: orderHashID,
      first_name: formData.first_name,
      last_name: formData.last_name,
      address: formData.address,
      zip: formData.zip,
      city: formData.city,
      country_id: orderDeliveryDetails.country_id,
      phone: orderDeliveryDetails.phone,
      company: formData.company || '',
    }, [http.MIDDLEWARES.CUSTOMER_ID])
      .then(() => {
        setIsSubmitting(false);
        onSuccess();
      })
      .catch((error: any) => {
        setIsSubmitting(false);
        onError(error);
      });
  };

  const handleSubmit = async (formData: CustomerAddressCreation) => {
    if (isSubmitting) return;

    if (!clientConfig.SKIP_ADRRESS_VALIDATION) {
      const params = new URLSearchParams({
        StreetAddress: formData.address,
        City: formData.city,
        PostalCode: formData.zip,
        CountryCode: orderDeliveryDetails.country_id,
        APIKey: clientConfig.BYTEPLANT_API_KEY,
      });

      try {
        const res = await fetch(`https://api.address-validator.net/api/verify?${params.toString()}`);
        const response: ByteplantApiResponse = await res.json();

        if (['SUSPECT', 'INVALID'].includes(response.status)) {
          setPendingFormData(formData);
          setByteplantState({ showModal: true, responseData: response });
          return;
        }
      } catch (_) {
        // On error, proceed without blocking the customer
      }
    }

    postAddressChange(formData);
  };

  const handleByteplantUseSuggestion = (suggestion: ByteplantApiResponse) => {
    const hasSuggestion = suggestion.street && suggestion.streetnumber && suggestion.city && suggestion.postalcode;
    const updatedFormData: CustomerAddressCreation = {
      ...pendingFormData!,
      ...(hasSuggestion && {
        address: `${suggestion.street} ${suggestion.streetnumber}`,
        city: suggestion.city,
        zip: suggestion.postalcode,
      }),
    };
    setByteplantState({ showModal: false, responseData: null });
    postAddressChange(updatedFormData);
  };

  const handleByteplantKeepOriginal = () => {
    setByteplantState({ showModal: false, responseData: null });
    postAddressChange(pendingFormData!);
  };

  const handleClose = () => {
    if (closeable) {
      setIsVisible(false);
      onClose?.();
    }
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (closeable && modalRef.current && !modalRef.current.contains(e.target as Node)) {
      handleClose();
    }
  };

  if (!isVisible) return null;

  return (
    <>
      {!byteplantState.showModal && (
        <div className="change-address-overlay" onClick={handleOverlayClick}>
          <div className="change-address-modal" ref={modalRef}>
            <div className="change-address-header">
              <h1 className="change-address-title">
                {translator.translate(language, 'my_account', 'MyAccountChangeAddress/title') || 'Please submit a new shipping address for redelivery'}
              </h1>
              {closeable && (
                <button className="change-address-close-btn" onClick={handleClose} type="button" aria-label="Close">
                  <img src={closeIcon} alt="close" />
                </button>
              )}
            </div>

            <div className="change-address-content">
              <MyAccountChangeAddressForm
                onSubmit={handleSubmit}
                language={language}
                country={country}
                defaultFormValues={defaultFormValues}
                countryOptions={countryOptions}
                isGoogleApiLoaded={isGoogleApiLoaded}
                isSubmitting={isSubmitting}
                submitButtonLabel={translator.translate(language, 'my_account', 'DeliveryAddressV2/submit') || 'Submit'}
              />
            </div>
          </div>
        </div>
      )}

      <PortaledModal
        shouldBackgroundClickHideModal={false}
        shouldShowModal={byteplantState.showModal}
        backgroundColor="rgba(13, 44, 84, 0.36)"
        hideModal={() => setByteplantState({ showModal: false, responseData: null })}
      >
        <ByteplantAddressValidatorModal
          responseData={byteplantState.responseData}
          language={language}
          handleCustomerUsingSuggestion={handleByteplantUseSuggestion}
          handleCustomerIgnoreSuggestion={handleByteplantKeepOriginal}
          onClose={() => setByteplantState({ showModal: false, responseData: null })}
        />
      </PortaledModal>
    </>
  );
};

export default MyAccountChangeAddress;
